import {
  AnyTriggerTransition,
  ButtonClickTriggerConfigs,
  createPlaceholderStepId,
  getValueByTransitionPath,
  isInternalStepId,
  isPlaceholderStep,
  isPlaceHolderStepId,
  isWhenUserResponse,
  RoleChangeTriggerConfigs,
  SendStepsCommon,
  StepConfigSendSurvey,
  TriggerForEditor,
  TriggerStepCreate,
  TriggerStepTestCaptcha,
  TriggerTransitionCreate,
  whenEqual,
  WhenUserResponse,
} from "@dots-projects/types";
import produce, { Draft } from "immer";
import {
  addPlaceholderNode,
  createNodesAndEdges,
  TRIGGER_ROOT_ID,
} from "../createNodeAndEdges";
import {
  copyPositions,
  initTriggerLayout,
  layoutAfterAddition,
  layoutAfterDeletion,
  layoutAfterUpdate,
} from "../createLayout";
import { getNode, isOverlap } from "./stateUtils";
import { WithHistory, undoable } from "./undoable";
import {
  TriggerState,
  TriggerEditorAction,
  EditorState,
  FlowState,
  TriggerEditorState,
  DeleteSteps,
} from "./reducerTypes";
import { applyNodeChanges, Node } from "reactflow";
import { syncWithTemplate } from "../stepTemplates";
import { nanoid } from "nanoid";
import invariant from "ts-invariant";
import { dfsSteps, getEntryStep } from "./triggerUtils";

/**
 * This is the root reducer used in the trigger editor. For ease of maintenance, it is divided into a few slice reducers. We didn't use `combineReducers` since we want the slice reducers to be applied in a specific order and some reducers require data from other reducers as input.
 *
 * @param state
 * @param action
 * @returns new state if the reducer the action (e.g. in a switch case), otherwise the original state
 */
export function triggerEditorReducer(
  state: TriggerEditorState,
  action: TriggerEditorAction
) {
  const triggerState0 = internalTriggerReducer(state.trigger, action);
  const triggerState = undoableTriggerReducer(triggerState0, action);
  const editorState = editorReducer(state.editor, action, state.flow);
  const flowState = flowReducer(
    state.flow,
    action,
    triggerState.current,
    editorState
  );

  return {
    trigger: triggerState,
    editor: editorState,
    flow: flowState,
  };
}

/**
 *
 * @param outputSteps This array will be mutated
 */
export function cleanupIdleSteps(outputSteps: TriggerStepCreate[]) {
  removeIdleStepsThatPointToNothing(outputSteps);
  removeDanglingIdleSteps(outputSteps);
}

/**
 * Remove idle steps with no transitions (or only idle transitions)
 *
 * E.g. ( [] = idle step )
 *
 * [] -> [non-idle], [], []
 * The result will be: [] -> [non-idle]
 *
 * [non-idle] -> [] -> [] -> []
 * The result will be: [non-idle]
 *
 * @param outputSteps This array will be mutated
 */
function removeIdleStepsThatPointToNothing(outputSteps: TriggerStepCreate[]) {
  // Initially danglingIdleStepIds includes all idle step ids
  const stepIdsToRemove = new Set<string>();
  // Starting from all idle steps, use depth-first search to
  let pointsToSomething = false;
  outputSteps.forEach((step) => {
    if (step.type === "idle") {
      pointsToSomething = false;
      dfsSteps(step, outputSteps, (step) => {
        if (step.type !== "idle" || isPlaceholderStep(step)) {
          pointsToSomething = true;
          return false; // shortcurcuit it
        } else {
          return true;
        }
      });
      if (!pointsToSomething && !isPlaceholderStep(step)) {
        stepIdsToRemove.add(step.id);
      }
    }
  });
  removeStepsAndIncomingTransitions(outputSteps, stepIdsToRemove);
}

/**
 * Remove idle steps that have no incoming transitions from any other steps.
 *
 * E.g. ( [] = idle step )
 *
 * [non-idle] -> [], [], []
 * The result will be [non-idle] -> []
 *
 * [non-idle] -> [] -> [] -> []
 * The result will be [non-idle] -> [] -> [] -> []
 *
 * @param outputSteps This array will be mutated
 */
function removeDanglingIdleSteps(outputSteps: TriggerStepCreate[]) {
  // Initially danglingIdleStepIds includes all idle step ids
  const danglingIdleStepIds = new Set(
    outputSteps.filter((s) => s.type === "idle").map((s) => s.id)
  );
  // Starting from all non-idle steps, use depth-first search to remove any steps in the path from danglingIdleStepIds. The remaining items after DFS will be dangling idle step ids.
  outputSteps.forEach((step) => {
    if (step.type !== "idle") {
      // console.log("====> starting from", step.id);
      dfsSteps(step, outputSteps, (s) => {
        // console.log("====>>", s.id);
        danglingIdleStepIds.delete(s.id);
      });
    }
  });
  // Remove dangling idle steps
  removeStepsAndIncomingTransitions(outputSteps, danglingIdleStepIds);
}

/**
 * Remove steps and any transitions that point to them. The `outputSteps` array will be mutated.
 *
 * @param outputSteps
 * @param stepIdsToRemove
 */
function removeStepsAndIncomingTransitions(
  outputSteps: TriggerStepCreate[],
  stepIdsToRemove: Set<string>
) {
  // remove steps
  for (let i = outputSteps.length - 1; i >= 0; i--) {
    if (stepIdsToRemove.has(outputSteps[i].id)) {
      outputSteps.splice(i, 1);
    }
  }
  // Remove incoming transitions
  outputSteps.forEach((step) => {
    for (let i = step.transitions.length - 1; i >= 0; i--) {
      if (stepIdsToRemove.has(step.transitions[i].targetStepId)) {
        step.transitions.splice(i, 1);
      }
    }
  });
}

/**
 * Keep the source step in sync with the deleted transition. For example, if the deleted transition is a button transition, we need to remove the corresponding button from the send_survey step.
 *
 * @param deletedTransition
 * @param outputStep
 */
function syncSourceStepAfterTransitionDeletion(
  deletedTransition: AnyTriggerTransition,
  outputStep: Draft<TriggerStepCreate>
) {
  if (outputStep.type === "send_survey") {
    if (
      "when" in deletedTransition &&
      isWhenUserResponse(deletedTransition.when) &&
      deletedTransition.when.userResponse.type === "button"
    ) {
      const buttonId = deletedTransition.when.userResponse.buttonId;
      const optionIndex = outputStep.configs.options.findIndex(
        (option) => typeof option === "object" && option.id === buttonId
      );
      outputStep.configs.options.splice(optionIndex, 1);
    }
  }
}

/**
 *
 * triggerReducer handles updates to the `trigger` state, including adding/updating/deleting steps and any changes to the trigger configs.
 *
 * @param state
 * @param action
 * @returns new state if the reducer the action (e.g. in a switch case), otherwise the original state
 */
function triggerReducer(state: TriggerState, action: TriggerEditorAction) {
  const replaceStepRefs = (
    oldStepId: string,
    newStepId: string,
    outputState: Draft<TriggerState>
  ) => {
    const idx = outputState.steps.findIndex((s) => s.id === oldStepId);
    outputState.steps.splice(idx, 1);
    outputState.steps.forEach((s) => {
      // elseStep
      if (s.if?.elseStepId === oldStepId) {
        s.if.elseStepId = newStepId;
      }
      // transitions
      s.transitions.forEach((t) => {
        if (t.targetStepId === oldStepId) {
          t.targetStepId = newStepId;
        }
      });
    });
  };

  const setFeedbackBaseOnStep = (
    outputState: Draft<TriggerState>,
    stepId: string,
    feedback: boolean
  ) => {
    const step = outputState.steps.find((s) => s.id === stepId);
    invariant(step, "step should exist");

    if (step.type === "open_modal") {
      //Find parent step
      const parentStep = outputState.steps.find((s) => {
        return s.transitions.some((t) => t.targetStepId === stepId);
      });

      // If parent step is not found then its a root step and we need to disabled feedback from
      // trigger config
      if (!parentStep) {
        (outputState.configs as ButtonClickTriggerConfigs).skipDefer = feedback;
        invariant(outputState.isDraft);
        (
          outputState.liveTriggerConfigs.configs as ButtonClickTriggerConfigs
        ).skipDefer = feedback;
      } else {
        (parentStep.configs as StepConfigSendSurvey).feedback = {
          disable: feedback,
        };
      }
    }
  };

  switch (action.type) {
    case "add_steps":
      return produce(state, (draft) => {
        // The `.map((s) => ({ ...s }))` below is to make a copy of all steps so that we could mutate it, because the steps are generated by immer elsewhere which are made read-only by immer's autoFreeze feature.
        const newSteps = action.payload.steps.map((s) => ({
          ...s,
          transitions: [...s.transitions],
        }));
        // @ts-ignore TODO fix this type error
        draft.steps.push(...newSteps);
        // TODO: we should correct the meaning of parentStep here and in action.payload.parentStepId, since sometimes it's really not precisely the parent of the new nodes (it's a placeholder that'll be replaced)!
        const parentStep = draft.steps.find(
          (s) => s.id === action.payload.parentStepId
        );
        const firstStep = newSteps[0];
        const leaves = newSteps.filter((s) => s.transitions.length === 0);

        // Connect to the parent
        if (parentStep) {
          if (isPlaceholderStep(parentStep)) {
            // replace it
            replaceStepRefs(parentStep.id, firstStep.id, draft);
            // preserve outgoing transitions of the placeholder
            leaves.forEach(
              (leaf) => (leaf.transitions = parentStep.transitions.slice(0))
            );

            setFeedbackBaseOnStep(draft, firstStep.id, true);
          } else {
            // insert to corresponding transition
            const { when, transitionIndex } = action.payload;

            //If the step is internal we just add the transition to its parent.
            if (isInternalStepId(firstStep.id)) {
              // internal steps could have a when object if we add a new branch on a conditional node
              if (when) {
                parentStep.transitions.push({
                  targetStepId: firstStep.id,
                  //@ts-ignore TODO fix this type error
                  when,
                });
              } else {
                parentStep.transitions.push({
                  targetStepId: firstStep.id,
                });
              }
            } else if (typeof transitionIndex === "number") {
              const transition = parentStep.transitions[transitionIndex];
              const oldTargetId = transition.targetStepId;
              transition.targetStepId = firstStep.id;
              leaves.forEach((leaf) =>
                leaf.transitions.push({ targetStepId: oldTargetId, when })
              );
            } else {
              // if no transitionIndex, which means we are adding steps via an implicit placeholder, we push a new transition
              invariant(
                when || parentStep.transitions.length === 0,
                `If neither "transitionIndex" or "when" is specified, we can only insert the node when the parent has no transitions but it's ${JSON.stringify(
                  parentStep.transitions
                )}.`
              );
              //@ts-ignore TODO fix this type error
              parentStep.transitions.push({ targetStepId: firstStep.id, when });
            }
          }
        } else {
          if (action.payload.isParentTriggerRoot) {
            const oldEntryStep =
              draft.steps.find((s) => s.isEntry) ?? draft.steps[0];
            oldEntryStep.isEntry = false;
            firstStep.isEntry = true;
            leaves.forEach(
              (leaf) => (leaf.transitions = [{ targetStepId: oldEntryStep.id }])
            );
          }
        }
        cleanupIdleSteps(draft.steps);
        cleanChannelReferences(newSteps as TriggerStepCreate[], draft);
        if (action.payload.relayout) {
          draft.steps = layoutAfterAddition(
            newSteps.map((s) => s.id),
            draft.steps
          );
        }
        setFeedbackBaseOnStep(draft, firstStep.id, true);
      });
    case "update_step":
      return produce(state, (draft) => {
        const updatedStep = { ...action.payload.step };
        const idx = draft.steps.findIndex((s) => s.id === updatedStep.id);
        if (idx >= 0) {
          const originalStep = draft.steps[idx];
          draft.steps[idx] = updatedStep;
          syncWithTemplate(originalStep, updatedStep, draft.steps);
        }
        cleanupIdleSteps(draft.steps);
        fixChannelTransitionsBaseOnUpdateSteps(draft.steps, updatedStep);
        draft.steps = layoutAfterUpdate(updatedStep.id, draft.steps);
      });
    case "delete_steps":
      return produce(state, (draft) => {
        let { steps } = draft;
        const deletedSteps: TriggerStepCreate[] = [];

        const stepsToDelete = getAllStepsToDelete(
          action.payload.stepIds,
          steps
        );

        for (let i = steps.length - 1; i >= 0; i--) {
          const step = steps[i];
          if (stepsToDelete.stepIds.includes(step.id)) {
            // delete the step at index i
            setFeedbackBaseOnStep(draft, step.id, false);
            steps.splice(i, 1);
            // It's important to get deleted steps from the original state instead of from the draft. Otherwise it'll leak the immer proxy object which causes issues like TypeError: Cannot perform 'get' on a proxy that has been revoked.
            deletedSteps.push(state.steps.find((s) => s.id === step.id)!);
          }
        }

        let newPlaceholderIds: string[] = [];
        deletedSteps.forEach((deletedStep) => {
          const newStepsIds = fixTransitionsBaseOnDeletedSteps(
            steps,
            deletedStep,
            stepsToDelete.connectParentTo
          );
          if (newStepsIds && newStepsIds.length > 0) {
            newPlaceholderIds = newPlaceholderIds.concat(newStepsIds);
          }
        });
        cleanupIdleSteps(steps);
        cleanChannelReferencesUponDelete(deletedSteps, draft);

        const checkEntryStep = getEntryStep(draft.steps);
        draft.steps = layoutAfterDeletion(deletedSteps, steps);
        // layoutAfterDeletion may have changed the entry step because it sorts the steps by position, so we need to update the entry step here.
        const entryStep = draft.steps.find((s) => s.id === checkEntryStep.id);
        if (entryStep) {
          entryStep.isEntry = true;
        }
        // We organize the steps by position after deletion to make sure steps are in the correct order.
        if (draft.steps.length)
          draft.steps = layoutAfterAddition(
            draft.steps.map((s) => s.id),
            draft.steps
          );
      });
    case "delete_transition":
      return produce(state, (draft) => {
        const { stepId, transitionIndex } = action.payload;
        const sourceStep = draft.steps.find((s) => s.id === stepId);
        if (sourceStep) {
          const [deletedTransition] = sourceStep.transitions.splice(
            transitionIndex,
            1
          );
          syncSourceStepAfterTransitionDeletion(deletedTransition, sourceStep);
        }
        cleanupIdleSteps(draft.steps);
      });
    case "connect_step":
      return produce(state, (draft) => {
        const {
          payload: { sourceOrPlaceholderStepId, targetStepId, when },
        } = action;
        if (isPlaceHolderStepId(sourceOrPlaceholderStepId)) {
          replaceStepRefs(sourceOrPlaceholderStepId, targetStepId, draft);
          const placeholderIdx = draft.steps.findIndex(
            (s) => s.id === sourceOrPlaceholderStepId
          );
          if (placeholderIdx >= 0) {
            draft.steps.splice(placeholderIdx, 1);
          }
        } else {
          // In this case the placeholder we are dragging is an implicit one.
          const sourceStep = draft.steps.find(
            (s) => s.id === sourceOrPlaceholderStepId
          );
          if (sourceStep) {
            const transitions =
              sourceStep.transitions as TriggerTransitionCreate[];
            const alreadyExists = transitions.some(
              (t) => t.targetStepId === targetStepId && whenEqual(when, t.when)
            );
            if (!alreadyExists) {
              sourceStep.transitions.push({
                targetStepId,
                //@ts-ignore TODO fix this type error
                when,
              });
            }
          }
        }
      });

    case "update_trigger_config":
      return produce(state, (draft) => {
        return {
          ...draft,
          configs: action.payload.draftConfig.configs,
          liveTriggerConfigs: action.payload.liveTriggerConfigs,
        };
      });

    case "update_button_click_trigger_config":
      return produce(state, (draft) => {
        const { message, htmlMessage, buttonText, buttonEmoji, channelId } =
          action.payload;
        const updatedConfigs = {
          type: draft.type,
          configs: {
            ...draft.configs,
            $$editorButtonClickContent: {
              message,
              buttonText,
              buttonEmoji,
              channelId,
              htmlMessage,
            },
          },
        };
        return {
          ...draft,
          ...updatedConfigs,
          liveTriggerConfigs: {
            type: draft.type,
            configs: {
              ...draft.configs,
              $$editorButtonClickContent: {
                message,
                buttonText,
                buttonEmoji,
                channelId,
                htmlMessage,
              },
            },
          },
        };
      });
    case "update_trigger_name":
      return produce(state, (draft) => {
        const { name } = action.payload;
        return {
          ...draft,
          name,
        };
      });
    case "update_role_change_trigger_config":
      return produce(state, (draft) => {
        const { newTrigger } = action.payload;
        const roleChangeTriggerConfig =
          newTrigger.configs as RoleChangeTriggerConfigs;
        return {
          ...draft,
          type: draft.type,
          configs: {
            ...roleChangeTriggerConfig,
          },
          liveTriggerConfigs: {
            type: draft.isDraft ? draft.liveTriggerConfigs.type : draft.type,
            configs: {
              ...roleChangeTriggerConfig,
            },
          },
        };
      });
    case "save_position":
      return produce(state, (draft) => {
        const { payload } = action;
        switch (payload.nodeType) {
          case "trigger":
            draft.configs = {
              ...draft.configs,
              $$editorPosition: payload.position,
            };
            break;
          case "step":
          case "condition":
            {
              const stepId = payload.stepId;
              const step = draft.steps.find((s) => s.id === stepId);
              invariant(step, `Can't find step ${stepId}`);
              if (payload.nodeType === "step") {
                step.configs = {
                  ...step.configs,
                  $$editorPosition: payload.position,
                };
              } else {
                step.configs = {
                  ...step.configs,
                  $$editorPositionWhenNode: payload.position,
                };
              }
            }
            break;
        }
      });
  }
  return state;
}

function deleteSendMultipleSelect(
  stepToBeDeleted: TriggerStepCreate,
  steps: TriggerStepCreate[]
): DeleteSteps {
  const [firstTransition] = stepToBeDeleted.transitions;

  let ifStep = steps.find((step) => step.id === firstTransition.targetStepId)!;
  let noIfStep: TriggerStepCreate;
  while (true) {
    if (ifStep.if) {
      ifStep = steps.find((step) => step.id === ifStep.if!.elseStepId)!;
    }

    if (!ifStep.if) {
      noIfStep = ifStep;
      break;
    }
  }

  function createGraph(
    step: TriggerStepCreate,
    steps: TriggerStepCreate[],
    graph: { [key: string]: string[] } = {}
  ) {
    // If the step id is noIfStep.id means that we have reached the end of the multiple select
    if (step.id === noIfStep.id) {
      return;
    }

    if (!graph[step.id]) {
      graph[step.id] = step.transitions.map((t) => t.targetStepId);
    }

    for (const children of step.transitions) {
      if (!graph[children.targetStepId]) {
        const child = steps.find((s) => s.id === children.targetStepId);
        createGraph(child!, steps, graph);
      }
    }
    return graph;
  }
  const deleteStepsGraph = createGraph(stepToBeDeleted, steps);

  // Check if there is a multi-select that is connected to this multi-select

  const multipleSelectParent = steps.find((step) => {
    return step.if && step.if.elseStepId === stepToBeDeleted.id;
  });

  if (multipleSelectParent) {
    multipleSelectParent.if!.elseStepId = noIfStep.id;
  }

  return {
    stepIds: Object.keys(deleteStepsGraph!),
    connectParentTo: noIfStep.id,
  };
}

/**
 *
 * @param captchaStep
 * @param steps all steps in the trigger. This function will mutate this array by adding a placeholder step. Ideally we shouldn't do this here in a function named "getXXX".
 * @returns the ids of the steps to be deleted, also creates a placeholder step and instructs the caller to connect the parent of the captcha step to the placeholder step
 */
function getStepsToDeleteForCaptcha(
  captchaStep: TriggerStepCreate,
  steps: TriggerStepCreate[]
): DeleteSteps {
  invariant(
    captchaStep.type === "test_captcha",
    `Expected captcha step, got ${captchaStep.type}`
  );
  // TODO maybe this code should be moved to fixTransitionsBasedonDeletedSteps
  const placeholderStep: TriggerStepCreate = {
    id: createPlaceholderStepId(nanoid(6) + "-captcha"),
    type: "idle",
    configs: {
      $$editorPosition: {
        x: captchaStep.configs?.$$editorPosition?.x ?? 0,
        y: (captchaStep.configs?.$$editorPosition?.y ?? 0) - 50, // Move the placeholder up a bit so that the steps below can move up after the captcha step is deleted
      },
    },
    transitions: [],
  };
  steps.push(placeholderStep);
  return {
    stepIds: [
      captchaStep.id,
      ...captchaStep.transitions.map((t) => t.targetStepId),
    ],
    connectParentTo: placeholderStep.id,
  };
}

function getAllStepsToDelete(
  stepIds: string[],
  steps: TriggerStepCreate[]
): DeleteSteps {
  const [firstStepId] = stepIds;

  const firstStep = steps.find((s) => s.id === firstStepId)!;

  switch (firstStep.type) {
    case "send_multiple_select":
      return deleteSendMultipleSelect(firstStep, steps);
    case "test_captcha":
      return getStepsToDeleteForCaptcha(firstStep, steps);
    default:
      return {
        stepIds,
      };
  }
}

/**
 * Connect the parent of a deleted step to its child. This is to make the chain remain connected.
 *
 * In some cases, we'll replace the deleted step with a placeholder step.
 *
 * In this function, we look at the transitions and "else steps" of all existing steps. If any of
 * the transitions or the "else steps" point at the deleted step, we create a new placeholder step
 * and update the matching transitions and "else steps" to point to the new placeholder.
 *
 * @param outputSteps
 * @param deletedStep
 * @param connectParentTo
 */
function fixTransitionsBaseOnDeletedSteps(
  outputSteps: TriggerStepCreate[],
  deletedStep: TriggerStepCreate,
  connectParentTo?: string
) {
  let newPlaceholderId: string;
  const newStepsIds: string[] = [];
  const transitionIdsToIgnore: string[] = [];

  //Check if the deleted step points to an internal step (e.g. a create_channel step that points to an internal delete channel)
  deletedStep.transitions.forEach((t) => {
    const transitionStep = outputSteps.find((s) => s.id === t.targetStepId)!;
    if (
      transitionStep &&
      isInternalStepId(transitionStep.id) &&
      transitionStep.type !== "idle"
    ) {
      const internalStepIndex = outputSteps.findIndex(
        (s) => s.id === t.targetStepId
      );
      outputSteps.splice(internalStepIndex, 1);
    }
  });

  /**
   * If the deleted step is pointed by more of one transition then we need to create a placeholder step
   * for every transition that points to the deleted step.
   */
  const deleteStepIsPointedByNTransitions = outputSteps
    .map((s) => s.transitions)
    .flat()
    .filter((t) => t.targetStepId === deletedStep.id).length;

  if (deleteStepIsPointedByNTransitions > 1) {
    outputSteps.forEach((s) => {
      s.transitions.forEach((t) => {
        if (t.targetStepId === deletedStep.id) {
          const placeholderStepId = createPlaceholderStepId(
            nanoid(6) + "transition"
          );
          outputSteps.push({
            id: placeholderStepId,
            type: "idle",
            configs: {},
            transitions: [],
          });
          t.targetStepId = placeholderStepId;
          newStepsIds.push(placeholderStepId);
          transitionIdsToIgnore.push(t.targetStepId);
        }
      });
    });
  }

  // check the transitions of all steps to see if any point at the deleted step
  // if so, attempt to update the transitions with the deleted step's next target step
  outputSteps.forEach((s) => {
    const transitions = s.transitions as TriggerTransitionCreate[];
    transitions
      .filter((t) => !transitionIdsToIgnore.includes(t.targetStepId))
      .forEach((t, index) => {
        if (
          /**
           *  Ways how an internal step can be deleted:
           * 1. A user unchecks "delete_channel after minutes" in create_channel form
           */
          deletedStep.id === t.targetStepId &&
          isInternalStepId(deletedStep.id) &&
          deletedStep.type !== "idle"
        ) {
          s.transitions.splice(index, 1);
        } else if (deletedStep.id === t.targetStepId) {
          const firstTransition = deletedStep.transitions[0];
          let transitionTargetId = firstTransition?.targetStepId;

          // If the targetStep doesn't have an unconditional transition, we create a placeholder.
          if (!firstTransition) {
            if (!transitionTargetId) {
              // create a new placeholder step if one doesn't exist already
              if (!newPlaceholderId) {
                const placeholderStepId = createPlaceholderStepId(
                  nanoid(6) + "transition"
                );
                outputSteps.push({
                  id: placeholderStepId,
                  type: "idle",
                  configs: {
                    $$editorPosition: deletedStep.configs?.$$editorPosition,
                  },
                  transitions: [],
                });
                newPlaceholderId = placeholderStepId;
                transitionTargetId = placeholderStepId;
              } else {
                transitionTargetId = newPlaceholderId;
              }
            }
          }

          //Parent step is an if branch so the parent needs to be updated to point to a placeholder.
          if (s.if) {
            const placeholderStepId = createPlaceholderStepId(
              nanoid(6) + "transition"
            );
            outputSteps.push({
              id: placeholderStepId,
              type: "idle",
              configs: {
                $$editorPosition: deletedStep.configs?.$$editorPosition,
              },
              transitions: [{ targetStepId: transitionTargetId }],
            });
            transitionTargetId = placeholderStepId;
          }

          // update the transition's target step id
          t.targetStepId = connectParentTo || transitionTargetId;
        }
      });

    // check the "else step ids" of all steps to see if any point at the deleted step
    // if so, attempt to update the "else step ids" with the deleted step's next target step
    if (s.if?.elseStepId) {
      if (deletedStep.id === s.if?.elseStepId) {
        const firstTransition = deletedStep.transitions[0];
        let elseTargetId = firstTransition?.targetStepId;

        // If the targetStep doesn't have an unconditional transition, we create a placeholder
        if (!firstTransition) {
          if (!elseTargetId) {
            if (!newPlaceholderId) {
              const placeholderStepId = createPlaceholderStepId(
                nanoid(6) + "transition"
              );
              outputSteps.push({
                id: placeholderStepId,
                type: "idle",
                configs: {
                  $$editorPosition: deletedStep.configs?.$$editorPosition,
                },
                transitions: [],
              });
              newPlaceholderId = placeholderStepId;
              elseTargetId = placeholderStepId;
            } else {
              elseTargetId = newPlaceholderId;
            }
          }
        }
        // update the "else step id" to point to the new target
        s.if.elseStepId = elseTargetId;
      }
    }
  });

  if (newStepsIds.length > 0) {
    return newStepsIds;
  }
  return null;
}

/**
 * Change channelId transitions base on updated channel id
 * For example for ask for email step, the transitions needs to be updated every time
 * the channel changes.
 * @param outputSteps
 * @param updatedStep
 */
function fixChannelTransitionsBaseOnUpdateSteps(
  outputSteps: TriggerStepCreate[],
  updatedStep: TriggerStepCreate
) {
  /**
   * We don't update channels for send_multiple_select
   * because it always points to an if condition step
   * meaning that the channel is already set when the step is created
   */

  if (updatedStep && updatedStep?.type !== "send_multiple_select") {
    const updatedChannelId = (updatedStep.configs as SendStepsCommon).channelId;

    //If the updated step has a channelId we update the channelId with the new one
    if (updatedChannelId) {
      const newDraft = produce(updatedStep, (updatedStepDraft) => {
        updatedStepDraft.transitions.forEach((transition) => {
          const transitionChannelId = getValueByTransitionPath(
            transition,
            "when.userResponse.channelId"
          );

          // If the updated step has a transition with a channelId we update the channelId with the new one\
          // transitionChhanel id can be an empty if the trigger is coming from a duplicate trigger
          // take a look to the function duplicateTrigger.ts
          if (transitionChannelId || transitionChannelId === "") {
            (transition.when as WhenUserResponse).userResponse.channelId =
              updatedChannelId;
          } else {
            const conditionStep = outputSteps.find(
              (step) => step.id === transition.targetStepId
            );

            //If the step is idle and has transitions with a when that has a channelId condition
            // then the step is a conditional step
            if (conditionStep?.type === "idle") {
              conditionStep.transitions.forEach((t) => {
                const oldConditionStepChannelId = getValueByTransitionPath(
                  t,
                  "when.userResponse.channelId"
                );

                if (oldConditionStepChannelId) {
                  (t.when as WhenUserResponse).userResponse.channelId =
                    updatedChannelId;
                }
              });
            } else if (conditionStep?.type === "test_captcha") {
              invariant(conditionStep.type === "test_captcha");

              conditionStep.configs.channelId = updatedChannelId;
              const transitionSteps = conditionStep.transitions.map(
                (t) => t.targetStepId
              );
              outputSteps
                .filter((step) => transitionSteps.includes(step.id))
                .forEach((step) => {
                  step.transitions.forEach((t) => {
                    const oldConditionStepChannelId = getValueByTransitionPath(
                      t,
                      "when.userResponse.channelId"
                    );

                    if (oldConditionStepChannelId) {
                      (t.when as WhenUserResponse).userResponse.channelId =
                        updatedChannelId;
                    }
                  });
                });
            }
          }
        });
      });
      const freezeUpdatedStepIndex = outputSteps.findIndex(
        (s) => s.id === updatedStep.id
      );
      outputSteps[freezeUpdatedStepIndex] = newDraft;
    }
  }
}

function cleanChannelReferencesUponDelete(
  deletedSteps: TriggerStepCreate[],
  state: Draft<TriggerState>
) {
  const [firstStep] = deletedSteps;

  const transitionStepsIds = firstStep.transitions.map((t) => t.targetStepId);
  state.steps
    .filter((step) => {
      return transitionStepsIds.includes(step.id);
    })
    .forEach((step) => {
      switch (step.type) {
        case "test_captcha":
          cleanChannelReferences([step], state);
      }
    });
}

/**
 *
 * @param crudSteps steps that can be created, updated or deleted
 * @param state
 */
function cleanChannelReferences(
  crudSteps: TriggerStepCreate[],
  state: Draft<TriggerState>
) {
  const [firstStep] = crudSteps;
  switch (firstStep.type) {
    case "test_captcha":
      return updateTestCaptchaChannelReferences(firstStep, state);
  }
}

function updateTestCaptchaChannelReferences(
  newStep: TriggerStepCreate,
  state: Draft<TriggerState>
) {
  invariant(newStep.type === "test_captcha");

  const parentStep = state.steps.find((s) => {
    const transitions = s.transitions.map((t) => t.targetStepId);
    return transitions.includes(newStep.id as string);
  });

  if (parentStep && (parentStep.configs as SendStepsCommon).channelId) {
    replaceTestCaptchaChannelReferences(
      newStep,
      state.steps,
      (parentStep.configs as SendStepsCommon).channelId
    );
  } else {
    invariant(state.isDraft);
    if (
      ["button_click", "slash_command"].includes(
        state.liveTriggerConfigs.type
      ) &&
      newStep.configs.ephemeral
    ) {
      replaceTestCaptchaChannelReferences(
        newStep,
        state.steps,
        "{{interaction.channelId}}"
      );
    } else {
      console.error("No channel id found for test captcha step");
    }
  }
}

function replaceTestCaptchaChannelReferences(
  step: TriggerStepCreate,
  currentSteps: TriggerStepCreate[],
  newChannelId: string
) {
  invariant(step.type === "test_captcha");

  const stepsToBeUpdated: TriggerStepCreate[] = [];
  const transitionSteps = step.transitions.map((t) => t.targetStepId);

  stepsToBeUpdated.push(
    produce(step, (newStepDraft) => {
      (newStepDraft as TriggerStepTestCaptcha).configs.channelId = newChannelId;

      currentSteps
        .filter((step) => transitionSteps.includes(step.id))
        .forEach((step) => {
          const stepUpdated = produce(step, (stepDraft) => {
            stepDraft.transitions.forEach((t) => {
              (t.when as WhenUserResponse).userResponse.channelId =
                newStepDraft.configs.channelId;
            });
          });
          stepsToBeUpdated.push(stepUpdated);
        });
    })
  );

  stepsToBeUpdated.forEach((step) => {
    //replace the step in the triggerSteps array
    const stepIndex = currentSteps.findIndex((s) => s.id === step.id);
    currentSteps[stepIndex] = step;
  });
}

/**
 *
 * Handles editor state such as isPersisting flag and selection.
 *
 * Note this reducer could act on any action that might update the editor state. For example, with action `add_steps`, the first added step will be selected.
 *
 * @param state
 * @param action
 * @param flow
 * @returns new state if the reducer the action (e.g. in a switch case), otherwise the original state
 */
function editorReducer(
  state: EditorState,
  action: TriggerEditorAction,
  flow: FlowState
) {
  switch (action.type) {
    case "$$update_is_persisting":
      return produce(state, (draft) => {
        draft.isPersisting = action.payload;
      });
    case "$$update_persist_response":
      return produce(state, (draft) => {
        draft.latestPersistResponses[action.payload.kind] =
          action.payload.response;
      });
    case "add_steps":
      if (
        action.payload.steps.length > 0 &&
        !isPlaceholderStep(action.payload.steps[0])
      ) {
        return produce(state, (draft) => {
          draft.selection = [action.payload.steps[0].id];
        });
      }
      break;
    case "update_step":
      return state;
    case "unselect":
      return produce(state, (draft) => {
        draft.selection = [];
      });
    case "select_node":
      return produce(state, (draft) => {
        draft.selection = [action.payload.nodeId];
        const node = getNode(flow.nodes, action.payload.nodeId);
        if (node?.type === "condition") {
          const firstEdge = flow.edges.find((e) => e.source === node.id);
          if (firstEdge) {
            draft.selection.push(firstEdge.id);
          }
        }
        // Clean error object
        draft.errors = {
          ...draft.errors,
          root: false,
          stepId: undefined,
        };
      });
    case "select_edge":
      return produce(state, (draft) => {
        draft.selection = [action.payload.edgeId];
        flow.edges.forEach((e) => {
          if (e.id === action.payload.edgeId) {
            const sourceNode = getNode(flow.nodes, e.source);
            if (sourceNode.type === "condition") {
              draft.selection.push(sourceNode.id);
            }
          }
        });
      });
    case "select_transition":
      return produce(state, (draft) => {
        flow.edges.forEach((e) => {
          //TODO need a better way to get the conditional node id
          const expectedSource = action.payload.whenAsString
            ? `userResponse-${action.payload.stepId}`
            : action.payload.stepId;
          const isSpecifiedTransition =
            e.source === expectedSource &&
            e.target === action.payload.targetStepId &&
            e.data?.whenAsString === action.payload.whenAsString;
          if (isSpecifiedTransition) {
            const conditionNode = getNode(flow.nodes, e.source);
            draft.selection = [e.id, conditionNode.id];
          }
        });
      });
    case "show_modal":
      return produce(state, (draft) => {
        draft.visibleModalId = action.payload.modalId;
      });
    case "hide_modal":
      return produce(state, (draft) => {
        draft.visibleModalId = undefined;
      });
    case "show_errors_box":
      return produce(state, (draft) => {
        draft.visibleErrorBoxId = action.payload.errorBoxId;
      });
    case "hide_errors_box":
      return produce(state, (draft) => {
        draft.visibleErrorBoxId = undefined;
      });
    case "set_trigger_type_error":
      return produce(state, (draft) => {
        draft.errors = {
          root: true,
        };
      });
    case "set_step_error":
      const stepId = action.payload.stepId;
      return produce(state, (draft) => {
        draft.errors = {
          stepId,
        };
      });
    case "hide_node_error_border":
      return produce(state, (draft) => {
        draft.errors = {
          root: false,
          stepId: undefined,
          changeViewportToNode: null,
        };
      });
    case "change_viewport":
      return produce(state, (draft) => {
        const { stepId, rootError } = action.payload;

        const step = stepId || (rootError ? TRIGGER_ROOT_ID : null);

        const node = flow.nodes.find((n) => n.id === step);

        draft.errors = {
          ...draft.errors,
          changeViewportToNode: node,
        };
      });
  }
  return state;
}

/**
 *
 * Handles updates to the nodes and edges of the flow chart.
 *
 * Note: `flow` is a derived state that is mostly generated from the source of truth `trigger.current` and `editor.selection`. This reducer acts on any action that might update trigger state or editor state.
 *
 * @param state
 * @param action
 * @param triggerState
 * @param editorState
 * @returns new state if the reducer the action (e.g. in a switch case), otherwise the original state
 *
 */
function flowReducer(
  state: FlowState,
  action: TriggerEditorAction,
  triggerState: TriggerState,
  editorState: EditorState
) {
  const syncSelection = (flow: FlowState) => {
    return produce(flow, (draft) => {
      draft.nodes.forEach(
        (n) => (n.selected = editorState.selection.includes(n.id))
      );
      draft.edges.forEach(
        (e) => (e.selected = editorState.selection.includes(e.id))
      );
    });
  };
  const recreateFlow = (trigger: TriggerForEditor) => {
    const { nodes, edges } = createNodesAndEdges(trigger);
    copyPositions(trigger, edges, nodes);

    return syncSelection({ nodes, edges });
  };
  const isDirectlyConnected = (n1: Node, n2: Node) =>
    state.edges.some(
      (e) =>
        (e.source === n1.id && e.target === n2.id) ||
        (e.source === n2.id && e.target === n1.id)
    );

  switch (action.type) {
    case "add_steps":
    case "update_step":
    case "delete_steps":
    case "delete_transition":
    case "undo":
    case "redo":
    case "$$replace_current_trigger":
    case "$$init_trigger_layout":
    case "connect_step":
      return recreateFlow(triggerState);
    case "unselect":
    case "select_node":
    case "select_edge":
    case "select_transition":
      return syncSelection(state);
    case "move_node":
      return produce(state, (draft) => {
        draft.nodes = applyNodeChanges(
          [action.payload.nodeChange],
          draft.nodes
        );
        const node = getNode(draft.nodes, action.payload.nodeChange.id);
        if (node.type === "placeholder") {
          draft.nodes.forEach(
            (n) =>
              (n.data.isDraggedOver =
                n !== node &&
                !isDirectlyConnected(n, node) &&
                isOverlap(n, node))
          );
        }
      });
    case "set_nodes_drag_over_flag":
      return produce(state, (draft) => {
        draft.nodes.forEach((n) => {
          const { trueNodeIds, falseNodeIds } = action.payload;
          if (
            trueNodeIds.includes(n.id) ||
            (trueNodeIds === "rest" && falseNodeIds.includes(n.id) === false)
          ) {
            n.data.isDraggedOver = true;
          }
          if (
            falseNodeIds.includes(n.id) ||
            (falseNodeIds === "rest" && trueNodeIds.includes(n.id) === false)
          ) {
            n.data.isDraggedOver = false;
          }
        });
      });
    case "add_floating_placeholder":
      return produce(state, (draft) => {
        const { id, data, position } = action.payload;
        // remove all other floating placeholders that are not selected
        draft.nodes = draft.nodes.filter(
          (n) => !(n.type === "placeholder" && n.data.isFloating && !n.selected)
        );
        addPlaceholderNode(id, data, position, draft.nodes);
      });
    case "remove_node":
      return produce(state, (draft) => {
        const index = draft.nodes.findIndex((n) => n.id === action.payload.id);
        if (index >= 0) {
          draft.nodes.splice(index, 1);
        }
      });
  }
  return state;
}

/**
 *
 * Handles internal updates to the trigger state
 *
 * We need this due to the way how trigger updates work on the backend. When we "update" a trigger on the backend (via an endpoint), we actually create a new trigger that links to the original one. This includes creating a draft (editing a published trigger), saving a draft and publishing a trigger. The only exception is toggling `isActive` of a published trigger, which is updated in place.
 *
 * The endpoints return the new trigger, and we want to update it in the editor. We don't want this operation to be undoable.
 *
 *
 * @param state
 * @param action
 * @returns new state if the reducer the action (e.g. in a switch case), otherwise the original state
 *
 */
function internalTriggerReducer(
  state: WithHistory<TriggerState>,
  action: TriggerEditorAction
) {
  switch (action.type) {
    case "$$init_trigger_layout":
      return produce(state, (draft) => {
        // Use type assertion to avoid "Exccessive stack depth comparing types" error on Amplify. It's strange that this error only happens on Amplify, but not locally.
        const trigger = draft.current as TriggerForEditor;
        const stepIdsWithoutPosition = initTriggerLayout(
          trigger,
          action.payload?.force
        );
        if (stepIdsWithoutPosition.length > 0) {
          trigger.steps = layoutAfterAddition(
            stepIdsWithoutPosition,
            trigger.steps
          );
        }
      });
    case "$$replace_current_trigger":
      return {
        ...state,
        current: action.payload.trigger,
        ...(action.payload.clearHistory ? { past: [], future: [] } : {}),
      };
  }
  return state;
}

const undoableTriggerReducer = undoable<TriggerState, TriggerEditorAction>(
  triggerReducer
);
